import { FieldHelperProps, FieldInputProps } from 'formik';
import invariant from 'invariant';
import React from 'react';
import usePreviousValue from 'utils/hooks/usePreviousValue';
import { InputFieldUIStates } from './InputField.config';
import { MessageProps } from './InputField.types';

export function useComputeInputFieldHandleChange<T>(
  { onChange }: FieldInputProps<T>,
  { setValue }: FieldHelperProps<T>,
  filterValue?: (val: any) => boolean,
  onChangeCallback?: (event: React.ChangeEvent<HTMLInputElement>) => void,
) {
  return React.useCallback(
    (event: any) => {
      if (
        typeof event === 'object' &&
        event !== null &&
        typeof event.target !== 'undefined'
      ) {
        if (typeof filterValue === 'function') {
          if (filterValue(event.target.value)) {
            onChange(event);
          }
        } else {
          onChange(event);
        }
      } else {
        if (typeof filterValue === 'function') {
          if (filterValue(event)) {
            setValue(event);
          }
        } else {
          setValue(event);
        }
      }
      onChangeCallback?.(event);
    },
    [filterValue, onChange, onChangeCallback, setValue],
  );
}

export function useTrackFieldInputFieldEvents(
  inputFieldState: InputFieldUIStates,
  messageProps: MessageProps,
  analyticsName?: string,
) {
  const messageId = messageProps?.id ?? null;
  const previousMessageId = usePreviousValue(messageId);

  // Only track 'errors'.
  const shouldTriggerFormErrorTracking =
    inputFieldState === InputFieldUIStates.error &&
    messageId !== previousMessageId;

  React.useEffect(() => {
    if (!shouldTriggerFormErrorTracking || !analyticsName) {
      return;
    }

    invariant(
      typeof messageId === 'string',
      'useTrackFieldInputFieldEvents: invalid InputFieldUI state',
    );
  }, [analyticsName, messageId, shouldTriggerFormErrorTracking]);
}
